import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import { useCallback, useEffect, useMemo, useState } from "react";
import theme from "@styles/theme";
import moment from "moment/moment";
import {
    IconCancel,
    IconChevronDown,
    IconChevronUp,
    IconClose,
    IconTime,
    IconWarningTriangle,
} from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { UsersMultiInputV2 } from "@features/users-input";
import { setNotification } from "@modules/notification";
import ShareResourceInfo from "@features/dialog/share/components/share-resource-info";
import { alpha } from "@mui/material";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";
import {
    addSharedUsersOfAsset,
    changeSharedUsersOfAsset,
    deleteSharedUsersOfAsset,
    getSharedUsersOfAsset,
} from "@modules/shared-box-v2";
import ApprovedUserTeamItemV2 from "@features/dialog/share/components/ApprovedUserTeamItemV2";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import fp from "lodash/fp";
import convertUtcToLocal from "@cores/convertUtcToLocal";

const DialogManageShareV2 = ({ selectedItem, onClickClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const DEFAULT_EXPIRES_IN_DAYS = 14;
    const DEFAULT_MAX_DOWNLOADS = 1;

    const user = useSelector(({ stage }) => stage.user);
    const stage = useSelector(({ stage }) => stage);
    const sharedRecDetail = useSelector(({ sharedBoxV2 }) => sharedBoxV2.sharedUsersOfAsset?.data);

    const [actionPending, setActionPending] = useState(false);
    const [selectedUsersOrTeams, setSelectedUsersOrTeams] = useState([]);
    const [approvedUsersOrTeams, setApprovedUsersOrTeams] = useState([]);
    const [deletedUsersOrTeams, setDeletedUsersOrTeams] = useState([]);

    const [accordionExpanded, setAccordionExpanded] = useState({});

    const getAccordionSummaryExpandIcon = useCallback(
        (id) => {
            if (accordionExpanded[id])
                return (
                    <Tooltip title={t("common::label::Fold")}>
                        <IconButton variant={"contained"} circled size={"small"} color={"secondary"}>
                            <IconChevronUp size={16} />
                        </IconButton>
                    </Tooltip>
                );
            else
                return (
                    <Tooltip title={t("common::label::Expand")}>
                        <IconButton circled size={"small"}>
                            <IconChevronDown size={16} />
                        </IconButton>
                    </Tooltip>
                );
        },
        [accordionExpanded],
    );

    const getWarningSharingMessage = useCallback(
        (user) => {
            let isDownloadLimit = false;
            let isExpired = false;
            if (user.maxDownloads && user.maxDownloads <= user.downloadCount) {
                isDownloadLimit = true;
            } else if (user.expiresAt) {
                isExpired = moment(user.expiresAt).isBefore(moment());
            }
            if (!(isDownloadLimit || isExpired)) return null;

            let warningMessage = "";
            if (isDownloadLimit && isExpired) warningMessage = "Sharing Period and Number of Downloads have";
            else if (isDownloadLimit) warningMessage = "Number of Downloads has";
            else if (isExpired) warningMessage = "Sharing Period has";

            return `${warningMessage} expired. You can reset it.`;
        },
        [approvedUsersOrTeams],
    );

    const onHandleChangeValue = (user, key, value) => {
        let updateData = { [key]: value };
        if (key === "unlimitedPeriod") {
            if (!value) {
                if (!user.expiresInDays) {
                    updateData.expiresInDays = DEFAULT_EXPIRES_IN_DAYS;
                }
                updateData.expiresAt = `${moment().add(user.expiresInDays, "d").format("YYYY-MM-DD")}`;
            }
        } else if (key === "unlimitedCount") {
            if (!value) updateData.maxDownloads = user.maxDownloads || DEFAULT_MAX_DOWNLOADS;
        }

        if (user.granteeId) {
            setApprovedUsersOrTeams(
                approvedUsersOrTeams.map((item) => (item.id === user.id ? { ...item, ...updateData } : item)),
            );
        } else {
            setSelectedUsersOrTeams(
                selectedUsersOrTeams.map((item) => (item.id === user.id ? { ...item, ...updateData } : item)),
            );
        }
    };

    const onHandleChangeExpireDays = useCallback(
        (id, days) => {
            const isAddedUser = selectedUsersOrTeams.find((item) => item.id === id);
            const expiresAt = `${moment().add(parseInt(days), "d").format("YYYY-MM-DD")}`;
            if (isAddedUser) {
                setSelectedUsersOrTeams(
                    selectedUsersOrTeams.map((item) =>
                        item.id === id ? { ...item, expiresInDays: parseInt(days), expiresAt } : item,
                    ),
                );
            } else {
                setApprovedUsersOrTeams(
                    approvedUsersOrTeams.map((item) =>
                        item.id === id ? { ...item, expiresInDays: parseInt(days), expiresAt } : item,
                    ),
                );
            }
        },
        [approvedUsersOrTeams, selectedUsersOrTeams],
    );

    const onHandleChangeMaximumDownloads = useCallback(
        (id, downloads) => {
            const isAddedUser = selectedUsersOrTeams.find((item) => item.id === id);
            if (isAddedUser) {
                setSelectedUsersOrTeams(
                    selectedUsersOrTeams.map((item) =>
                        item.id === id ? { ...item, maxDownloads: parseInt(downloads) } : item,
                    ),
                );
            } else {
                setApprovedUsersOrTeams(
                    approvedUsersOrTeams.map((item) =>
                        item.id === id ? { ...item, maxDownloads: parseInt(downloads), downloadCount: 0 } : item,
                    ),
                );
            }
        },
        [approvedUsersOrTeams, selectedUsersOrTeams],
    );

    const editedUsersOrTeams = useMemo(() => {
        const _selectedUsersOrTeams = [...selectedUsersOrTeams].reverse();
        return _selectedUsersOrTeams.concat(approvedUsersOrTeams);
    }, [selectedUsersOrTeams, approvedUsersOrTeams]);

    const getAccordionSummaryText = useCallback((user) => {
        const isExpired = user.expiresAt && moment(user.expiresAt).isBefore(moment());

        if (isExpired)
            return <Typography color={theme.palette.warning.main}>{t(`common::label::Expired`, `Expired`)}</Typography>;
        else if (user.unlimitedPeriod)
            return (
                <Typography color={theme.palette.primary.light}>
                    {t(`common::label::Unlimited`, `Unlimited`)}
                </Typography>
            );
        else {
            return (
                <Typography color={theme.palette.greySecondary[500]}>
                    {moment(user.expiresAt).format("YYYY-MM-DD")}
                </Typography>
            );
        }
    }, []);

    const onHandleClickDeleteUser = (user) => {
        const isAddedUser = selectedUsersOrTeams.find((item) => item.id === user.id);
        if (isAddedUser) setSelectedUsersOrTeams((prev) => prev.filter((item) => item.id !== user.id));
        else {
            setApprovedUsersOrTeams((prev) => prev.filter((item) => item.id !== user.id));
            setDeletedUsersOrTeams((prev) => [...prev, user]);
        }
    };

    const onHandleClickSubmit = useCallback(async () => {
        try {
            setActionPending(true);
            const changeGranteesList = approvedUsersOrTeams
                .filter((item) =>
                    sharedRecDetail?.some(
                        (shared) => shared.id === item.granteeId && !fp.isEqual(item, getParsedUser(shared)),
                    ),
                )
                .reduce((acc, current) => {
                    let data = {
                        shareId: current.granteeId,
                        expiresInDays: !current.unlimitedPeriod ? current.expiresInDays : null,
                        maxDownloads: !current.unlimitedCount ? current.maxDownloads : null,
                    };
                    acc.push(data);
                    return acc;
                }, []);

            const addGranteesList = selectedUsersOrTeams.reduce((acc, item) => {
                let data = {
                    sharedToId: item.id,
                };

                if (!item.unlimitedPeriod) data.option = { expiresInDays: item.expiresInDays };
                if (!item.unlimitedCount) data.option = { ...(data.option ?? {}), maxDownloads: item.maxDownloads };

                acc.push(data);
                return acc;
            }, []);

            const changePromises = Promise.all(
                changeGranteesList.map(async (item) => {
                    return await dispatch(
                        changeSharedUsersOfAsset({
                            assetId: selectedItem.id,
                            projectId: selectedItem.summary.project?.id,
                            data: item,
                            customErrorMessage: true,
                        }),
                    );
                }),
            );
            const addPromises = Promise.all(
                addGranteesList.map(async (item) => {
                    return await dispatch(
                        addSharedUsersOfAsset({
                            assetId: selectedItem.id,
                            projectId: selectedItem.summary.project?.id,
                            data: item,
                            customErrorMessage: true,
                        }),
                    );
                }),
            );
            const deletePromises = Promise.all(
                deletedUsersOrTeams.map(async (item) => {
                    return await dispatch(
                        deleteSharedUsersOfAsset({
                            assetId: selectedItem.id,
                            projectId: selectedItem.summary.project?.id,
                            shareId: item.granteeId,
                            customErrorMessage: true,
                        }),
                    );
                }),
            );

            await Promise.all([changePromises, addPromises, deletePromises]);

            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::Asset {{item}} has been shared`, `Asset {{item}} has been shared`, {
                        item: `'${selectedItem.name ?? selectedItem?.summary?.name}'`,
                    }),
                }),
            );
            onClickClose();
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(
                        `common::msg::There was an error sharing asset {{item}}`,
                        `There was an error sharing asset {{item}}`,
                        {
                            item: `'${selectedItem.name ?? selectedItem?.summary?.name}'`,
                        },
                    ),
                }),
            );
        } finally {
            setActionPending(false);
        }
    }, [user, selectedItem, sharedRecDetail, selectedUsersOrTeams, approvedUsersOrTeams, deletedUsersOrTeams]);

    const getParsedUser = useCallback(
        ({ shared, id, option }) => {
            const { expiresAt, maxDownloads, downloadCount } = option || {};
            let expiresInDays = !!expiresAt
                ? countDaysBetweentDates({
                      fromDate: moment().toISOString(),
                      toDate: expiresAt,
                  }) + 1 // +1 to include the current day
                : DEFAULT_EXPIRES_IN_DAYS;
            return {
                id: shared.to.id,
                granteeId: id,
                name: shared.to.name,
                email: shared.to.username,
                unlimitedPeriod: !expiresAt,
                unlimitedCount: !maxDownloads,
                expiresAt: expiresAt && convertUtcToLocal(expiresAt),
                expiresInDays,
                maxDownloads,
                downloadCount,
            };
        },
        [sharedRecDetail],
    );

    useEffect(() => {
        if (!sharedRecDetail) return;
        setApprovedUsersOrTeams(
            sharedRecDetail.map((item) => {
                return { ...getParsedUser(item), downloadCount: 0 };
            }),
        );
    }, [sharedRecDetail]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(
            getSharedUsersOfAsset(selectedItem.id, selectedItem.summary?.project?.id, { sharedById: stage?.user?.id }),
        );
    }, []);

    const isUnChanged = useMemo(() => {
        const originalUsers = sharedRecDetail?.map((item) => getParsedUser(item)) || [];
        const originalUsersWithoutExpiresAt = originalUsers.map((user) => fp.omit(["expiresAt"], user));
        const approvedUsersWithoutExpiresAt = approvedUsersOrTeams.map((user) => fp.omit(["expiresAt"], user));
        return (
            fp.isEqual(originalUsersWithoutExpiresAt, approvedUsersWithoutExpiresAt) &&
            selectedUsersOrTeams.length === 0 &&
            deletedUsersOrTeams.length === 0
        );
    }, [sharedRecDetail, approvedUsersOrTeams, selectedUsersOrTeams, deletedUsersOrTeams]);

    const exceptedUsers = useMemo(() => {
        return selectedUsersOrTeams.concat(approvedUsersOrTeams, user);
    }, [selectedUsersOrTeams, approvedUsersOrTeams, user]);

    return (
        <StickyHeaderDialog onClose={onClickClose} open variant="expandable" maxWidth={"lg"}>
            <DialogTitle>
                <Grid container>
                    <Typography variant={"h4_700"}>{t(`common::label::Share`, `Share`)}</Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`, "Close")}
                    onClickButton={onClickClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent
                sx={{
                    height: "100%",
                    "&.MuiDialogContent-root": { width: "776px", pl: 3, pr: 1.5, pt: 0, pb: 3 },
                }}
            >
                <Grid container flexDirection={"column"} gap={theme.spacing(3)}>
                    <ShareResourceInfo domain={"ASSET"} resource={selectedItem.summary} />
                    <Grid container flexDirection={"column"} gap={theme.spacing(1)}>
                        <Typography variant={`body1_500`}>{t(`common::label::Add user`, `Add user`)}</Typography>
                        <UsersMultiInputV2
                            users={selectedUsersOrTeams}
                            exceptUsers={exceptedUsers}
                            projectId={selectedItem.summary.project?.id}
                            sx={{
                                flex: 3,
                                "& .MuiOutlinedInput-root": {
                                    paddingRight: "0px !important",
                                },
                            }}
                            placeholder={
                                selectedUsersOrTeams.length === 0 &&
                                t(`common::msg::Please enter user's name.`, `Please enter user's name.`)
                            }
                            onChange={(users) => {
                                const addedUser = users.find(
                                    (user) => !selectedUsersOrTeams.find((u) => u.id === user.id),
                                );
                                if (addedUser) {
                                    setAccordionExpanded({
                                        ...accordionExpanded,
                                        [addedUser.id]: true, // 새로 추가된 사용자의 아코디언을 확장
                                    });
                                }
                                const _users = users.map((user) => ({
                                    ...user,
                                    expiresInDays: DEFAULT_EXPIRES_IN_DAYS,
                                    maxDownloads: DEFAULT_MAX_DOWNLOADS,
                                    unlimitedPeriod: true,
                                    unlimitedCount: true,
                                }));
                                setSelectedUsersOrTeams(_users);
                            }}
                        />
                    </Grid>
                    <Grid container flexDirection={"column"} gap={theme.spacing(1)}>
                        <Grid item>
                            <Typography variant={`body1_500`}>
                                {t(`common::label::Shared users`, `Shared users`)}
                                {` `}
                                <Typography variant={`body1_500`} color={theme.palette.primary.main}>
                                    {editedUsersOrTeams.length > 0 && editedUsersOrTeams.length}
                                </Typography>
                            </Typography>
                        </Grid>

                        <Grid
                            container
                            flexDirection={"row"}
                            sx={{
                                borderRadius: "4px",
                                border: `1px solid ${theme.palette.greySecondary[100]}`,
                            }}
                        >
                            {editedUsersOrTeams?.length === 0 ? (
                                <Grid container alignItems={"center"} justifyContent={"center"} p={theme.spacing(2)}>
                                    {t(`common::msg::There are no shared users.`, `There are no shared users.`)}
                                </Grid>
                            ) : (
                                <>
                                    {editedUsersOrTeams?.map((user) => (
                                        <Accordion
                                            key={user.id}
                                            expanded={accordionExpanded[user.id] || false}
                                            sx={{
                                                width: "100%",
                                                border: "none",
                                                borderBottom: `1px solid ${theme.palette.greySecondary[100]} !important`,
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAccordionExpanded({
                                                    ...accordionExpanded,
                                                    [user.id]: !accordionExpanded[user.id],
                                                });
                                            }}
                                        >
                                            <AccordionSummary
                                                sx={{
                                                    width: "100%",
                                                    padding: `0 ${theme.spacing(1)} 0 0 !important`,
                                                    ".MuiAccordionSummary-content": {
                                                        margin: "0px",
                                                        "&.Mui-expanded": {
                                                            margin: "0px",
                                                        },
                                                    },
                                                    "&.Mui-expanded": {
                                                        backgroundColor: alpha(
                                                            theme.palette.greySecondary["A100"],
                                                            0.24,
                                                        ),
                                                        borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                                                        minHeight: 56,
                                                        margin: "0px",
                                                    },
                                                    pointerEvents: "auto",
                                                }}
                                            >
                                                <Grid container flexDirection={"row"}>
                                                    <Grid item flex={1}>
                                                        <ApprovedUserTeamItemV2 userOrTeam={user} />
                                                    </Grid>
                                                    <Grid item sx={{ display: "flex", alignItems: "center" }}>
                                                        {!accordionExpanded[user.id] && (
                                                            <Box sx={{ mr: 1 }}>{getAccordionSummaryText(user)}</Box>
                                                        )}
                                                        {getAccordionSummaryExpandIcon(user.id)}
                                                        <Divider
                                                            flexItem
                                                            orientation={"vertical"}
                                                            sx={{
                                                                my: theme.spacing(1),
                                                                mx: theme.spacing(1.5),
                                                            }}
                                                        />
                                                        <IconButton
                                                            circled
                                                            size={"small"}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onHandleClickDeleteUser(user);
                                                            }}
                                                        >
                                                            <IconCancel size={12} />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    width: "100%",
                                                    padding: `${theme.spacing(3)} ${theme.spacing(3.5)}`,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Grid container flexDirection={"column"} gap={theme.spacing(1)}>
                                                    {getWarningSharingMessage(user) && (
                                                        <Grid
                                                            item
                                                            sx={{
                                                                mb: theme.spacing(2.5),
                                                            }}
                                                        >
                                                            <Alert
                                                                color={"warning"}
                                                                severity="warning"
                                                                icon={
                                                                    <IconWarningTriangle fontSize="small" height={16} />
                                                                }
                                                            >
                                                                <Typography variant={"body1_400"}>
                                                                    {t(
                                                                        `common::msg::${getWarningSharingMessage(
                                                                            user,
                                                                        )}`,
                                                                        getWarningSharingMessage(user),
                                                                    )}
                                                                </Typography>
                                                            </Alert>
                                                        </Grid>
                                                    )}
                                                    <Stack direction={"column"} gap={theme.spacing(2.5)}>
                                                        <Stack direction={"row"}>
                                                            <Grid
                                                                item
                                                                sx={{
                                                                    flexBasis: "120px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "flex-start",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant={`body1_400`}
                                                                    sx={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    {t(
                                                                        `common::label::Sharing Period`,
                                                                        `Sharing Period`,
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item sx={{ display: "flex", alignItems: "center" }}>
                                                                <Switch
                                                                    checked={user.unlimitedPeriod}
                                                                    onChange={(e) =>
                                                                        onHandleChangeValue(
                                                                            user,
                                                                            "unlimitedPeriod",
                                                                            e.target.checked,
                                                                        )
                                                                    }
                                                                    color="success"
                                                                />
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ verticalAlign: "middle" }}
                                                                >
                                                                    {t(`common::label::Unlimited`, "Unlimited")}
                                                                </Typography>
                                                                <Divider
                                                                    orientation="vertical"
                                                                    flexItem
                                                                    sx={{ mx: 2.5, my: 1 }}
                                                                />
                                                                <Typography
                                                                    variant={`body1_400`}
                                                                    sx={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: theme.spacing(0.5),
                                                                        color: alpha(
                                                                            theme.palette.greySecondary.main,
                                                                            user.unlimitedPeriod ? 0.3 : 1,
                                                                        ),
                                                                    }}
                                                                >
                                                                    {t(`common::label::Maximum`, `Maximum`)}
                                                                    <TextField
                                                                        type={"number"}
                                                                        size={"small"}
                                                                        inputProps={{
                                                                            min: 1,
                                                                        }}
                                                                        sx={{
                                                                            width: "80px",
                                                                            input: {
                                                                                paddingLeft: theme.spacing(2),
                                                                            },
                                                                        }}
                                                                        value={user?.expiresInDays}
                                                                        onKeyDown={(e) => {
                                                                            if (e.code === "Minus") {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                        onChange={(e) => {
                                                                            onHandleChangeExpireDays(
                                                                                user.id,
                                                                                e.target.value > 0 ? e.target.value : 1,
                                                                            );
                                                                        }}
                                                                        disabled={user.unlimitedPeriod}
                                                                    />
                                                                    <Grid
                                                                        item
                                                                        sx={{ display: "flex", alignItems: "center" }}
                                                                    >
                                                                        <Typography
                                                                            component="span"
                                                                            sx={{
                                                                                mr: 2,
                                                                                color: alpha(
                                                                                    theme.palette.greySecondary.main,
                                                                                    user.unlimitedPeriod ? 0.3 : 1,
                                                                                ),
                                                                            }}
                                                                        >
                                                                            {t("common::label::days", "days")}
                                                                        </Typography>
                                                                        {!user.unlimitedPeriod && (
                                                                            <>
                                                                                <IconTime
                                                                                    size={16}
                                                                                    htmlColor={`#999`}
                                                                                    sx={{ mr: 0.5 }}
                                                                                />
                                                                                {t(`common::label::Expires At`)} :
                                                                                <Typography
                                                                                    variant={`body1_400`}
                                                                                    color={theme.palette.warning.main}
                                                                                >
                                                                                    {`${moment(user.expiresAt).format(
                                                                                        "YYYY-MM-DD",
                                                                                    )}`}
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </Grid>
                                                                </Typography>
                                                            </Grid>
                                                        </Stack>

                                                        <Stack direction={"row"}>
                                                            <Grid
                                                                item
                                                                sx={{
                                                                    flexBasis: "120px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "flex-start",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant={`body1_400`}
                                                                    sx={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    {t(
                                                                        `common::label::Number of Downloads`,
                                                                        `Number of Downloads`,
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item sx={{ display: "flex", alignItems: "center" }}>
                                                                <Switch
                                                                    checked={user.unlimitedCount}
                                                                    onChange={(e) =>
                                                                        onHandleChangeValue(
                                                                            user,
                                                                            "unlimitedCount",
                                                                            e.target.checked,
                                                                        )
                                                                    }
                                                                    color="success"
                                                                />
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ verticalAlign: "middle" }}
                                                                >
                                                                    {t(`common::label::Unlimited`, "Unlimited")}
                                                                </Typography>
                                                                <Divider
                                                                    orientation="vertical"
                                                                    flexItem
                                                                    sx={{ mx: 2.5, my: 1 }}
                                                                />
                                                                <Typography
                                                                    variant={`body1_400`}
                                                                    sx={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: theme.spacing(0.5),
                                                                        color: alpha(
                                                                            theme.palette.greySecondary.main,
                                                                            user.unlimitedCount ? 0.3 : 1,
                                                                        ),
                                                                    }}
                                                                >
                                                                    {t(`common::label::Maximum`, `Maximum`)}
                                                                    <TextField
                                                                        type={"number"}
                                                                        size={"small"}
                                                                        inputProps={{
                                                                            min: 1,
                                                                        }}
                                                                        sx={{
                                                                            width: "80px",
                                                                            input: {
                                                                                paddingLeft: theme.spacing(2),
                                                                            },
                                                                        }}
                                                                        value={
                                                                            user?.maxDownloads || DEFAULT_MAX_DOWNLOADS
                                                                        }
                                                                        onKeyDown={(e) => {
                                                                            if (e.code === "Minus") {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                        onInput={(e) => {
                                                                            onHandleChangeMaximumDownloads(
                                                                                user.id,
                                                                                e.target.value > 0 ? e.target.value : 1,
                                                                            );
                                                                        }}
                                                                        disabled={user.unlimitedCount}
                                                                    />
                                                                    {t(`common::label::times`, `times`)}
                                                                </Typography>
                                                            </Grid>
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider flexItem />
            <DialogActions>
                <Button color="secondary" onClick={onClickClose} variant="text">
                    {t(`common::label::Cancel`, `Cancel`)}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    loading={actionPending}
                    onClick={onHandleClickSubmit}
                    disabled={isUnChanged || actionPending}
                >
                    {t(`common::label::Apply`, "Apply")}
                </Button>
            </DialogActions>
        </StickyHeaderDialog>
    );
};

export default DialogManageShareV2;
